/**
 * Currency choices for the finance calculators.
 *
 * Amounts are formatted through `Intl` (via the widget's `useToolNumber`) with the
 * selected ISO 4217 code, so the symbol, grouping and decimal mark follow the reader's
 * locale and the minor unit follows the currency (0 decimals for the yen, 2 for the
 * dollar). `PLAIN` shows bare numbers for readers who want no currency at all.
 */

export const PLAIN = "none";

export const CURRENCY_CODES = [
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "NZD",
  "CHF",
  "JPY",
  "CNY",
  "INR",
  "KRW",
  "SGD",
  "MXN",
  "BRL",
  "ZAR",
  "SEK",
] as const;

export type CurrencyChoice = (typeof CURRENCY_CODES)[number] | typeof PLAIN;

/** Decimal places in the currency's minor unit (2 for a plain number). */
export function currencyDigits(code: string): number {
  if (code === PLAIN) {
    return 2;
  }
  try {
    return (
      new Intl.NumberFormat("en", {
        style: "currency",
        currency: code,
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/** `Intl.NumberFormat` options that write an amount in `code`. */
export function moneyOptions(code: string): Intl.NumberFormatOptions {
  if (code === PLAIN) {
    return { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  }
  return { style: "currency", currency: code };
}

/** Compounding / payment frequencies offered across the finance tools. */
export const FREQUENCY_VALUES = {
  annually: 1,
  semiannually: 2,
  quarterly: 4,
  bimonthly: 6,
  monthly: 12,
  semimonthly: 24,
  biweekly: 26,
  weekly: 52,
  daily: 365,
} as const;

export type FrequencyKey = keyof typeof FREQUENCY_VALUES;
